import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  updateApiKey,
  type ApiKey,
  type CreatedApiKey,
} from './api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';

export function ApiKeysPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const keys = useQuery({
    queryKey: ['apikeys', orgId],
    queryFn: () => listApiKeys(orgId),
    enabled: !!orgId,
  });

  const [name, setName] = useState('');
  const [type, setType] = useState<'publishable' | 'secret'>('publishable');
  const [cidrsInput, setCidrsInput] = useState('');
  const [cidrsError, setCidrsError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<CreatedApiKey | null>(null);

  const create = useMutation({
    mutationFn: () => {
      // Empty input on a secret key means "no restriction" — we send an
      // empty array explicitly so the backend records the intent rather
      // than us omitting the field (which would also work, but is less
      // self-documenting on the audit log side).
      const cidrs = type === 'secret' ? parseCidrs(cidrsInput) : [];
      return createApiKey(orgId, { name, type, allowed_cidrs: cidrs });
    },
    onSuccess: async (k) => {
      setJustCreated(k);
      setName('');
      setCidrsInput('');
      await qc.invalidateQueries({ queryKey: ['apikeys', orgId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(orgId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['apikeys', orgId] }),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCidrsError(null);
    if (type === 'secret' && cidrsInput.trim()) {
      const err = validateCidrs(cidrsInput);
      if (err) {
        setCidrsError(err);
        return;
      }
    }
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">API keys</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Two key types, each for a different surface of your integration.
        </p>
      </div>

      <Card>
        <CardBody>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-good/[0.08] px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-eyebrow text-good">
                  pk_live_…
                </span>
                <h3 className="text-sm font-medium text-ink-primary">Publishable</h3>
              </div>
              <p className="mt-2 text-sm text-ink-secondary">
                Embedded in your frontend. Configures the <code className="font-mono text-xs">@sigilkeys/sdk</code> and the
                wallet iframe. Identifies your company; safe to ship in browser bundles.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-warn/[0.08] px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-eyebrow text-warn">
                  sk_live_…
                </span>
                <h3 className="text-sm font-medium text-ink-primary">Secret</h3>
              </div>
              <p className="mt-2 text-sm text-ink-secondary">
                Server-to-server only. Used to call <code className="font-mono text-xs">GET /v1/s2s/wallets</code> and
                related back-office endpoints. Never expose in a browser; rotate if leaked. Optionally pin to a list of source CIDRs below.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {justCreated && (
        <div className="rounded-md border border-warn/30 bg-warn/[0.06] p-4 text-sm">
          <p className="font-medium text-warn">Copy this key now — you won't see it again.</p>
          <pre className="mt-3 overflow-x-auto rounded border border-warn/30 bg-white px-3 py-2 font-mono text-xs">
            {justCreated.cleartext}
          </pre>
          <button
            onClick={() => setJustCreated(null)}
            className="mt-3 text-xs font-medium text-warn underline"
          >
            I saved it. Dismiss.
          </button>
        </div>
      )}

      <Card>
        <CardHeader title="Create a new key" />
        <CardBody>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_180px_120px] sm:items-end">
              <Input label="Key name" value={name} onChange={(e) => setName(e.target.value)} required />
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-primary">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as 'publishable' | 'secret')}
                  className="block h-10 w-full rounded-md border border-line bg-white px-3 text-sm focus:border-ink-primary focus:outline-none focus:ring-1 focus:ring-ink-primary"
                >
                  <option value="publishable">Publishable</option>
                  <option value="secret">Secret</option>
                </select>
              </div>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? '…' : 'Create'}
              </Button>
            </div>

            {type === 'secret' && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-primary">
                  Allowed source IPs <span className="text-ink-tertiary">(optional)</span>
                </label>
                <Input
                  value={cidrsInput}
                  onChange={(e) => {
                    setCidrsInput(e.target.value);
                    setCidrsError(null);
                  }}
                  placeholder="10.0.0.0/24, 203.0.113.7/32"
                  className="font-mono text-xs"
                />
                <p className="text-xs text-ink-tertiary">
                  Comma-separated CIDRs. When set, this key is rejected from any other source IP.
                  Leave empty for no restriction (default).
                </p>
                {cidrsError && <p className="text-xs text-bad">{cidrsError}</p>}
              </div>
            )}
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Active keys" />
        <CardBody>
          {keys.isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
          {keys.data && keys.data.length === 0 && <p className="text-sm text-ink-tertiary">No keys yet.</p>}
          {keys.data && keys.data.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-ink-tertiary">
                <tr className="border-b border-line-soft">
                  <th className="py-2 text-left font-medium">Name</th>
                  <th className="py-2 text-left font-medium">Type</th>
                  <th className="py-2 text-left font-medium">Prefix</th>
                  <th className="py-2 text-left font-medium">Allowed IPs</th>
                  <th className="py-2 text-left font-medium">Created</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-soft">
                {keys.data.map((k) => (
                  <KeyRow
                    key={k.id}
                    k={k}
                    orgId={orgId}
                    onRevoke={() => revoke.mutate(k.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function KeyRow({ k, orgId, onRevoke }: { k: ApiKey; orgId: string; onRevoke: () => void }) {
  const revoked = !!k.revoked_at;
  const isSecret = k.type === 'secret';
  const [editing, setEditing] = useState(false);
  return (
    <>
      <tr className={revoked ? 'opacity-50' : ''}>
        <td className="py-3">{k.name}</td>
        <td className="py-3 text-ink-secondary">{k.type}</td>
        <td className="py-3 font-mono text-xs">{k.key_prefix}…</td>
        <td className="py-3 text-ink-secondary">
          <AllowedCidrsCell key={`${k.id}-${k.allowed_cidrs.join(',')}`} cidrs={k.allowed_cidrs} isSecret={isSecret} />
        </td>
        <td className="py-3 text-ink-secondary">{new Date(k.created_at).toLocaleDateString()}</td>
        <td className="py-3 text-right">
          <div className="flex justify-end gap-2">
            {!revoked && isSecret && (
              <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Cancel' : 'Edit IPs'}
              </Button>
            )}
            {revoked ? (
              <span className="text-xs text-ink-tertiary">revoked</span>
            ) : (
              <Button variant="danger" onClick={onRevoke}>
                Revoke
              </Button>
            )}
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={6} className="bg-surface-soft px-4 py-4">
            <EditCidrsForm
              orgId={orgId}
              keyId={k.id}
              initial={k.allowed_cidrs}
              onDone={() => setEditing(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function AllowedCidrsCell({ cidrs, isSecret }: { cidrs: string[]; isSecret: boolean }) {
  if (!isSecret) {
    return <span className="text-xs text-ink-tertiary">—</span>;
  }
  if (cidrs.length === 0) {
    return <span className="text-xs text-ink-tertiary">no restriction</span>;
  }
  return <span className="font-mono text-xs text-ink-primary">{cidrs.join(', ')}</span>;
}

function EditCidrsForm({
  orgId,
  keyId,
  initial,
  onDone,
}: {
  orgId: string;
  keyId: string;
  initial: string[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial.join(', '));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (cidrs: string[]) => updateApiKey(orgId, keyId, { allowed_cidrs: cidrs }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['apikeys', orgId] });
      onDone();
    },
    onError: (e: unknown) => {
      setError(extractError(e));
    },
  });

  function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (trimmed) {
      const err = validateCidrs(trimmed);
      if (err) {
        setError(err);
        return;
      }
    }
    mutation.mutate(parseCidrs(trimmed));
  }

  return (
    <form onSubmit={handleSave} className="flex flex-wrap items-end gap-3">
      <div className="flex-1 space-y-1.5 min-w-[260px]">
        <label className="block text-xs font-medium text-ink-primary">
          Allowed source IPs (comma-separated CIDRs)
        </label>
        <Input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder="10.0.0.0/24, 203.0.113.7/32"
          className="font-mono text-xs"
        />
        {error && <p className="text-xs text-bad">{error}</p>}
        <p className="text-xs text-ink-tertiary">
          Empty = no restriction. Existing in-flight requests are not affected; new ones take effect immediately.
        </p>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ----- helpers -----

function parseCidrs(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// validateCidrs is intentionally permissive — the backend has the authoritative
// net.ParseCIDR check. We do a coarse shape check here so the user gets fast
// feedback on obvious typos (missing /N, non-numeric octets) without us
// reimplementing v4/v6 parsing in the browser.
function validateCidrs(raw: string): string | null {
  const items = parseCidrs(raw);
  if (items.length === 0) return null;
  for (const c of items) {
    if (!CIDR_SHAPE.test(c)) {
      return `"${c}" doesn't look like a CIDR (expected x.x.x.x/n or v6/n).`;
    }
  }
  return null;
}

// Loose shape: IPv4 with /0-32, OR a v6-ish blob with /0-128. We forward to
// the backend for authoritative parsing.
const CIDR_SHAPE = /^([0-9.]+\/(3[0-2]|[12]?[0-9])|[0-9a-fA-F:]+\/(12[0-8]|1[01][0-9]|[1-9]?[0-9]))$/;

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { error?: { message?: string } } } }).response;
    return r?.data?.error?.message ?? 'Update failed.';
  }
  if (e instanceof Error) return e.message;
  return 'Update failed.';
}
